import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import apolloClient from '../config/ApolloClient';
import useUserContext from '../config/UserContext';
import { grantorOrgsQuery, logoImagesForUserQuery, templateQuery } from '../services/Queries';

const stylesheet = {
    drawer: 'tw:flex tw:flex-col tw:bg-transparent',
    drawerSticky: 'tw:md:sticky tw:md:top-[50px] tw:md:z-[2234]',
    toggleButton: 'tw:p-0 tw:md:hidden tw:ml-3 tw:bg-transparent tw:border-none tw:text-[#212529] tw:text-[2rem] tw:shadow-none tw:cursor-pointer',
    overflow: 'tw:md:block',
    toc: 'tw:pt-4 tw:pb-4 tw:overflow-auto tw:md:mr-[-15px] tw:md:pr-[calc(15px+1rem)]',
    drawerHeader: 'tw:text-xl tw:no-underline tw:text-[rgba(26,26,26,0.75)] tw:mt-0',
    navBlock: 'tw:block',
    navItem: 'tw:block',
    navLink: 'tw:block tw:text-[90%] tw:no-underline tw:text-[rgba(26,26,26,0.75)] tw:hover:text-[#999] tw:transition-colors tw:duration-200 tw:pt-1 tw:pb-2 tw:px-4',
    activeNavLink: 'tw:block tw:text-[90%] tw:no-underline tw:transition-colors tw:duration-200 tw:pt-1 tw:pb-2 tw:px-4 tw:border-l-3 tw:border-l-laud-purple tw:text-laud-purple tw:bg-[#f0e4f2] tw:font-medium',
};

function isActive(pathname: string, href: string): boolean {
    if (href === '/admin') {
        return pathname.startsWith('/admin');
    }
    return pathname === href;
}

const SideBar = (props: Record<string, unknown>) => {
    const user = useUserContext();
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const isAdmin = user.attributes.admin || false;
    const isOrgAdmin = user.attributes.orgAdmin || false;
    const canGrant = user.attributes.canGrant || false;

    const linkClass = (href: string) => isActive(pathname, href) ? stylesheet.activeNavLink : stylesheet.navLink;

    const prefetchGrantData = useCallback(() => {
        const sub = user.attributes.sub;
        if (!sub) return;
        apolloClient.query({ query: templateQuery, variables: { UserName: sub } });
        apolloClient.query({ query: logoImagesForUserQuery, variables: { UserName: sub } });
        apolloClient.query({ query: grantorOrgsQuery, variables: { UserName: sub } });
    }, [user.attributes.sub]);

    return (
        <Disclosure as="div" className={`${stylesheet.drawer} ${stylesheet.drawerSticky}`} {...props}>
            <DisclosureButton className={stylesheet.toggleButton} aria-label="Toggle sidebar">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 30 30"
                  width="30"
                  height="30"
                  focusable="false"
                  aria-hidden="true"
                >
                    <title>Menu</title>
                    <path
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeMiterlimit="10"
                      d="M4 7h22M4 15h22M4 23h22"
                    />
                </svg>
            </DisclosureButton>
            <DisclosurePanel static className={stylesheet.overflow}>
                <div className={stylesheet.toc}>
                    <span className={stylesheet.drawerHeader}>{' '}</span>
                    <div className={stylesheet.navBlock}>
                        <div className={stylesheet.navItem}>
                            <a className={linkClass('/')} href="/">{t('nav.home')}</a>
                        </div>
                        {user.attributes.canGrant && (
                            <>
                                <div className={stylesheet.navItem}>
                                    <a className={linkClass('/team')} href="/team">{t('nav.team')}</a>
                                </div>
                                <div className={stylesheet.navItem}>
                                    <a className={linkClass('/calendar')} href="/calendar">{t('nav.calendar')}</a>
                                </div>
                                <div className={stylesheet.navItem}>
                                    <span onMouseEnter={prefetchGrantData}>
                                        <a className={linkClass('/grant')} href="/grant">{t('nav.mintAwards')}</a>
                                    </span>
                                </div>
                                <div className={stylesheet.navItem}>
                                    <a className={linkClass('/bulkMint')} href="/bulkMint">{t('nav.bulkMintAwards')}</a>
                                </div>
                                <div className={stylesheet.navItem}>
                                    <a className={linkClass('/grantlist')} href="/grantlist">{t('nav.mintingHistory')}</a>
                                </div>
                                <div className={stylesheet.navItem}>
                                    <a className={linkClass('/grantstats')} href="/grantstats">{t('nav.mintingStats')}</a>
                                </div>
                                <div className={stylesheet.navItem}>
                                    <a className={linkClass('/revoke')} href="/revoke">{t('nav.revokeAwards')}</a>
                                </div>
                            </>
                        )}
                        {(isOrgAdmin || canGrant) && (
                            <div className={stylesheet.navItem}>
                                <a className={linkClass('/listOrganizations')} href="/listOrganizations">{t('nav.admin')}</a>
                            </div>
                        )}
                        {isAdmin && (
                            <div className={stylesheet.navItem}>
                                <a className={linkClass('/admin')} href="/admin">{t('nav.siteAdmin')}</a>
                            </div>
                        )}
                    </div>
                </div>
            </DisclosurePanel>
        </Disclosure>
    );
};

export default memo(SideBar);
